/* eslint-disable @typescript-eslint/naming-convention */
import moment from 'moment';
import {
    get as _get,
    map as _map,
    chunk as _chunk,
    concat as _concat,
    flatten as _flatten,
    isEmpty as _isEmpty,
    toString as _toString,
} from 'lodash';

import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { AjaxResponse } from 'rxjs/ajax';

import { environment } from 'src/environments/environment';
import { BaseRequester } from '../common/base-requester';
import { unwrapLsiEnvelope } from '../common/lsi-envelope';
import { retryOnThrottle } from '../common/throttle-retry';
import { OrderStatus } from './enums/order-status';
import {
    TiktokAuthInfo,
    TiktokDelegateDownloadRequest,
    TiktokDelegateRequest,
} from './interfaces/tiktok-delegate-request';

@Injectable({
    providedIn: 'root',
})
export class TiktokOpenService extends BaseRequester {
    private tiktokAuthInfo = inject<TiktokAuthInfo>('tiktokAuthInfo' as any);

    private delegateEndpoint = environment.TiktokDelegateEndPoint;

    constructor() {
        super();
    }

    getOrderList(orderStatus: OrderStatus): Observable<unknown> {
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            method: 'post',
            servicePath: '/order/202309/orders/search',
            parameter: {
                page_size: 50,
            },
            body: {
                order_status: orderStatus,
                create_time_ge: moment().subtract(3, 'months').unix(),
            },
        };
        return this.requestTiktokDelegate(request);
    }

    getOrderDetail(orderIds: string[]): Observable<any[]> {
        if (_isEmpty(orderIds)) {
            return of([]);
        }
        const orderIdChunks: string[][] = _chunk(orderIds, 50);
        const requestObservers: Observable<any>[] = _map(
            orderIdChunks,
            (_orderIds: string[]) => {
                return this.requestTiktokDelegate({
                    authInfo: this.tiktokAuthInfo,
                    servicePath: '/order/202309/orders',
                    parameter: {
                        ids: `${_orderIds.toString()}`,
                    },
                });
            },
        );
        return forkJoin(requestObservers).pipe(
            map((responses: any[]) => {
                return _flatten(responses);
            }, take(1)),
        );
    }

    shipPackage(
        packageId: string,
        handoverMethod: 'PICKUP' | 'DROP_OFF',
    ): Observable<unknown> {
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            method: 'post',
            servicePath: `/fulfillment/202309/packages/${packageId}/ship`,
            parameter: {},
            body: {
                handover_method: handoverMethod,
            },
        };
        return this.requestTiktokDelegate(request);
    }

    getPackageShippingDocuments(packageId: string): Observable<unknown> {
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            servicePath: `/fulfillment/202309/packages/${packageId}/shipping_documents`,
            parameter: {
                document_type: 'SHIPPING_LABEL',
            },
        };
        return this.requestTiktokDelegate(request);
    }

    downloadShippingDocument(docUrl: string): Observable<string> {
        const request: TiktokDelegateDownloadRequest = {
            authInfo: this.tiktokAuthInfo,
            downloadUrl: docUrl,
        };
        return this.internalPostRequest<TiktokDelegateDownloadRequest>(
            this.delegateEndpoint,
            request,
        ).pipe(
            map((ajaxRes: AjaxResponse) => {
                const body = unwrapLsiEnvelope(_get(ajaxRes, 'response'));
                const contentBase64: string = _get(body, 'content_base64');
                if (!contentBase64) {
                    throw new Error('Delegate returned no document content');
                }
                return contentBase64;
            }, take(1)),
        );
    }

    // --- Finance -----------------------------------------------------------
    //
    // TikTok reports money per settlement statement, not per order: first list
    // the statements in the window, then walk each one's transactions to reach
    // order level.

    // Settlement statements in the window, paged by `next_page_token`.
    getStatements(
        statementTimeGe: number,
        statementTimeLe: number,
        pageToken?: string,
        accumItem?: unknown[],
    ): Observable<unknown[]> {
        const parameter: Record<string, unknown> = {
            page_size: 50,
            statement_time_ge: statementTimeGe,
            statement_time_le: statementTimeLe,
            sort_field: 'statement_time',
        };
        if (pageToken) {
            parameter['page_token'] = pageToken;
        }
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            servicePath: '/finance/202309/statements',
            method: 'get',
            parameter,
        };
        return this.requestTiktokDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page: unknown[] = _get(data, 'statements') || [];
                const accumulated = _concat(accumItem || [], page);
                const nextToken: string = _get(data, 'next_page_token');
                if (nextToken && page.length > 0) {
                    return this.getStatements(
                        statementTimeGe,
                        statementTimeLe,
                        nextToken,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // Order-level rows within one statement.
    //
    // ⚠️ `sort_field` is REQUIRED here. Omitting it returns HTTP 400 with
    // code 36009004 ("SortField is a required field and has not been
    // provided") rather than defaulting to anything.
    getStatementTransactions(
        statementId: string,
        pageToken?: string,
        accumItem?: unknown[],
    ): Observable<unknown[]> {
        const parameter: Record<string, unknown> = {
            page_size: 50,
            sort_field: 'order_create_time',
        };
        if (pageToken) {
            parameter['page_token'] = pageToken;
        }
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            servicePath: `/finance/202309/statements/${statementId}/statement_transactions`,
            method: 'get',
            parameter,
        };
        return this.requestTiktokDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page: unknown[] =
                    _get(data, 'statement_transactions') || [];
                const accumulated = _concat(accumItem || [], page);
                const nextToken: string = _get(data, 'next_page_token');
                if (nextToken && page.length > 0) {
                    return this.getStatementTransactions(
                        statementId,
                        nextToken,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // Every order in `orderStatus` created at or after `createTimeGe` (unix
    // seconds), walking next_page_token to the end — getOrderList above
    // stops at the first page of 50.
    searchOrders(
        orderStatus: OrderStatus,
        createTimeGe: number,
        pageToken?: string,
        accumItem?: unknown[],
    ): Observable<unknown[]> {
        const parameter: Record<string, unknown> = { page_size: 50 };
        if (pageToken) {
            parameter['page_token'] = pageToken;
        }
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            method: 'post',
            servicePath: '/order/202309/orders/search',
            parameter,
            body: {
                order_status: orderStatus,
                create_time_ge: createTimeGe,
            },
        };
        return this.requestTiktokDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page: unknown[] = _get(data, 'orders') || [];
                const accumulated = _concat(accumItem || [], page);
                const nextToken: string = _get(data, 'next_page_token');
                if (nextToken && page.length > 0) {
                    return this.searchOrders(
                        orderStatus,
                        createTimeGe,
                        nextToken,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // --- Returns -----------------------------------------------------------

    // Return / refund / replacement requests opened at or after
    // `createTimeGe` (unix seconds), newest first, paged by next_page_token.
    // POST: the filters travel in the body, paging in the query string.
    searchReturns(
        createTimeGe: number,
        pageToken?: string,
        accumItem?: unknown[],
    ): Observable<unknown[]> {
        const parameter: Record<string, unknown> = {
            page_size: 50,
            sort_field: 'create_time',
            sort_order: 'DESC',
        };
        if (pageToken) {
            parameter['page_token'] = pageToken;
        }
        const request: TiktokDelegateRequest = {
            authInfo: this.tiktokAuthInfo,
            method: 'post',
            servicePath: '/return_refund/202309/returns/search',
            parameter,
            body: { create_time_ge: createTimeGe },
        };
        return this.requestTiktokDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page: unknown[] = _get(data, 'return_orders') || [];
                const accumulated = _concat(accumItem || [], page);
                const nextToken: string = _get(data, 'next_page_token');
                if (nextToken && page.length > 0) {
                    return this.searchReturns(
                        createTimeGe,
                        nextToken,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    private requestTiktokDelegate(
        request: TiktokDelegateRequest,
    ): Observable<unknown> {
        return this.internalPostRequest<TiktokDelegateRequest>(
            this.delegateEndpoint,
            request,
        ).pipe(
            map((ajaxRes: AjaxResponse) => {
                const body = unwrapLsiEnvelope(_get(ajaxRes, 'response'));
                const responseCodeString: string = _toString(
                    _get(body, 'code'),
                );
                if (responseCodeString === '0') {
                    return _get(body, 'data');
                } else {
                    throw new Error(
                        `[${_get(body, 'code')}] ${_get(body, 'message')}`,
                    );
                }
            }, take(1)),
        );
    }
}
